import { useContext, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import uuid from "react-uuid";
import toast from "react-hot-toast";

import Sidebar from "../../../shared/components/sidebar/Sidebar";
import Navbar from "../../../shared/components/navbar/Navbar";
import { useHttpClient } from "../../../shared/hooks/http-hook";
import { AuthContext } from "../../../shared/context/auth-context";
import { ClinicaContext } from "../../../shared/context/clinica-context";
import {
  encontrarTratamentoEquivalente,
  motivoTratamentoSemEquivalente,
} from "../../../shared/util/tratamentoEquivalente";
import { useLoading } from "../../../shared/context/LoadingContext";

import ElementoOrcamentoDireto from "../components/ElementoOrcamentoDireto";
import NotaInformativa from "../../../shared/components/notaInformativa/NotaInformativa";

import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import BusinessIcon from "@mui/icons-material/Business";

import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from "@mui/material";

import "../../../shared/css/PlanoAvaliacaoOrcamento.scss";
import "../../../style/formError.scss";

const DENTES_CRIANCAS = [
  "51",
  "52",
  "53",
  "54",
  "55",
  "61",
  "62",
  "63",
  "64",
  "65",
  "71",
  "72",
  "73",
  "74",
  "75",
  "81",
  "82",
  "83",
  "84",
  "85",
  "Arcada Superior",
  "Arcada Inferior",
  "Arcadas",
];

const DENTES_ADULTOS = [
  "11",
  "12",
  "13",
  "14",
  "15",
  "16",
  "17",
  "18",
  "21",
  "22",
  "23",
  "24",
  "25",
  "26",
  "27",
  "28",
  "31",
  "32",
  "33",
  "34",
  "35",
  "36",
  "37",
  "38",
  "41",
  "42",
  "43",
  "44",
  "45",
  "46",
  "47",
  "48",
];

const DENTES = [...DENTES_ADULTOS, ...DENTES_CRIANCAS];

function NewOrcamentoDireto() {
  const { startLoading, stopLoading } = useLoading();
  const auth = useContext(AuthContext);
  const clinica = useContext(ClinicaContext);
  const { sendRequest } = useHttpClient();
  const navigate = useNavigate();
  const clienteId = useParams().clienteId;
  const orcamentoId = useParams().orcamentoId;

  // Estados básicos
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cliente, setCliente] = useState(null);
  const [tratamentos, setTratamentos] = useState([]);
  const [tratamentosAutocomplete, setTratamentosAutocomplete] = useState([]);
  const [shortcuts, setShortcuts] = useState([]);
  const [caixas, setCaixas] = useState([]);
  const [instituicoes, setInstituicoes] = useState([]);
  const [touched, setTouched] = useState(false);

  const [isDuplicacao, setIsDuplicacao] = useState(false);

  // Estado do médico responsável
  const [medicoResponsavel, setMedicoResponsavel] = useState(
    auth.role === "Secretária(o)" || auth.role === "Assistente"
      ? auth.usuariosAssociados.length === 1
        ? auth.usuariosAssociados[0].id
        : ""
      : auth.userId,
  );

  // Lista de serviços/tratamentos
  const [serviceList, setServiceList] = useState([]);

  // Estados de cobertura
  const [tipoCobertura, setTipoCobertura] = useState("PARTICULAR");
  const [instituicaoSelecionada, setInstituicaoSelecionada] = useState(null);
  const [modalidadePagamento, setModalidadePagamento] = useState("DIRETO");
  const [categoriaBeneficiario, setCategoriaBeneficiario] = useState("");

  // Estados financeiros
  const [subtotal, setSubtotal] = useState(0);
  const [subtotalCliente, setSubtotalCliente] = useState(0);
  const [total, setTotal] = useState(0);
  const [totalCobertura, setTotalCobertura] = useState(0);
  const [desconto, setDesconto] = useState(0);
  const [tipoDesconto, setTipoDesconto] = useState("$");
  const [observacoes, setObservacoes] = useState("");
  const [orcamentoIsentoPagamento, setOrcamentoIsentoPagamento] =
    useState(false);

  // Contadores
  const [nrTratamentosCobertos, setNrTratamentosCobertos] = useState(0);
  const [nrTratamentosNaoCobertos, setNrTratamentosNaoCobertos] = useState(0);

  // Parceria
  const [empresaParceira, setEmpresaParceira] = useState(null);
  const [parceriaValida, setParceriaValida] = useState(false);
  const [descontoParceriaAplicado, setDescontoParceriaAplicado] =
    useState(false);

  // Modais
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showDentesShortcutModal, setShowDentesShortcutModal] = useState(false);
  const [shortcutAtual, setShortcutAtual] = useState(null);
  const [dentesSelecionadosShortcut, setDentesSelecionadosShortcut] = useState(
    [],
  );

  // Modal para 2 orçamentos (MISTO)
  const [modalDescontoAberta, setModalDescontoAberta] = useState(false);
  const [descontoSegurado, setDescontoSegurado] = useState(0);
  const [descontoParticular, setDescontoParticular] = useState(0);
  const [opcaoRapidaSelecionada, setOpcaoRapidaSelecionada] = useState(null);

  // Fetch inicial de dados
  useEffect(() => {
    const fetchData = async () => {
      startLoading();
      try {
        const [
          tratamentosResponse,
          clienteResponse,
          shortcutsResponse,
          instituicoesResponse,
          caixasResponse,
        ] = await Promise.all([
          sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/tratamentos/populated/${clinica.clinica._id}`,
            "GET",
            null,
            { Authorization: "Bearer " + auth.token },
          ),
          sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/clientes/${clienteId}?clinicaId=${clinica.clinica._id}`,
            "GET",
            null,
            { Authorization: "Bearer " + auth.token },
          ),
          sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/shortcutsavaliacao/${clinica.clinica._id}`,
            "GET",
            null,
            { Authorization: "Bearer " + auth.token },
          ),
          sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/instituicoes/clinica/${clinica.clinica._id}`,
            "GET",
            null,
            { Authorization: "Bearer " + auth.token },
          ),
          sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/caixas/clinica/${clinica.clinica._id}`,
            "GET",
            null,
            { Authorization: "Bearer " + auth.token },
          ),
        ]);

        // Processar tratamentos
        const sortedTratamentos = sortTratamentos(
          tratamentosResponse.tratamentos,
        );
        setTratamentos(sortedTratamentos);
        if (orcamentoId && sortedTratamentos.length > 0) {
          try {
            const orcamentoResponse = await sendRequest(
              `${process.env.REACT_APP_BACKEND_LINK}/api/orcamentos/${orcamentoId}`,
              "GET",
              null,
              { Authorization: "Bearer " + auth.token },
            );

            const orcamentoOriginal = orcamentoResponse.orcamento;

            // Preencher lista de tratamentos
            /* Duplicar de OUTRA unidade: os ids nunca batem certo, porque cada
               unidade tem o seu catálogo com documentos próprios. O ajudante
               procura por id e, não achando, pelo nome.

               Não havendo equivalente, a linha fica SEM tratamento e com erro
               em vez de cair no primeiro da lista, como caía antes — isso
               produzia um orçamento cheio do tratamento errado, com os preços
               errados, sem nada a avisar. */
            let semEquivalente = 0;
            const tratamentosDuplicados = orcamentoOriginal.procedimentos.map(
              (proc) => {
                const tratamentoCompleto = encontrarTratamentoEquivalente(
                  proc.tratamento,
                  sortedTratamentos,
                );
                if (!tratamentoCompleto) semEquivalente++;

                return {
                  id: uuid(),
                  tratamento: tratamentoCompleto || null,
                  dentesTratados: proc.dente
                    ? proc.dente.split(", ").filter(Boolean)
                    : [],
                  faces: proc.faces || [],
                  show: true,
                  erro: tratamentoCompleto
                    ? ""
                    : motivoTratamentoSemEquivalente(proc.tratamento),
                };
              },
            );

            if (semEquivalente > 0) {
              toast.error(
                `${semEquivalente} tratamento(s) não existem no catálogo desta unidade. Escolha os correspondentes antes de guardar.`,
                { duration: 8000 },
              );
            }

            setServiceList(tratamentosDuplicados);
            setIsDuplicacao(true);
            setTouched(true);

            // Preencher cobertura se não for PARTICULAR
            if (
              orcamentoOriginal.tipoCobertura !== "PARTICULAR" &&
              orcamentoOriginal.instituicaoCobertura
            ) {
              const instituicao = instituicoesResponse.instituicoes.find(
                (inst) =>
                  inst._id === orcamentoOriginal.instituicaoCobertura._id ||
                  inst._id === orcamentoOriginal.instituicaoCobertura,
              );
              if (instituicao) {
                setInstituicaoSelecionada(instituicao);
                setModalidadePagamento(
                  orcamentoOriginal.modalidadePagamento || "DIRETO",
                );
                setCategoriaBeneficiario(
                  orcamentoOriginal.categoriaBeneficiario || "",
                );
              }
            }

            // Preencher observações
            if (orcamentoOriginal.observacoes) {
              setObservacoes(orcamentoOriginal.observacoes);
            }

            toast.success("Orçamento carregado para duplicação");
          } catch (err) {
            console.error("Erro ao carregar orçamento para duplicação:", err);
            toast.error("Erro ao carregar orçamento original");
          }
        }

        setTratamentosAutocomplete(
          formatTratamentosAutocomplete(sortedTratamentos),
        );

        // Inicializar lista com primeiro tratamento
        if (sortedTratamentos.length > 0 && !orcamentoId) {
          setServiceList([
            {
              id: uuid(),
              tratamento: sortedTratamentos[0],
              dentesTratados: [],
              faces: [],
              show: true,
              erro: "",
            },
          ]);
        }

        setCliente(clienteResponse.cliente);
        setShortcuts(shortcutsResponse.shortcuts || []);
        setInstituicoes(
          instituicoesResponse.instituicoes.filter((inst) => inst.ativo),
        );
        setCaixas(caixasResponse.caixas);

        // Verificar empresa parceira
        if (clienteResponse.cliente.empresaParceira) {
          try {
            const responseEmpresa = await sendRequest(
              `${process.env.REACT_APP_BACKEND_LINK}/api/empresasparceiras/verificar/${clienteResponse.cliente.empresaParceira}`,
              "GET",
              null,
              { Authorization: "Bearer " + auth.token },
            );

            if (responseEmpresa.parceriaValida) {
              setEmpresaParceira(responseEmpresa.empresa);
              setParceriaValida(true);
            }
          } catch (err) {
            console.error("Erro ao verificar empresa parceira:", err);
          }
        }
      } catch (err) {
        console.error("Erro ao buscar dados:", err);
        toast.error("Erro ao carregar dados");
      } finally {
        stopLoading();
      }
    };

    fetchData();
  }, [sendRequest, clinica, auth.token, clienteId]);

  // Recalcular valores quando serviceList ou cobertura mudam
  useEffect(() => {
    if (serviceList.length > 0 && tratamentos.length > 0) {
      updateValores();
    }
  }, [
    serviceList,
    instituicaoSelecionada,
    modalidadePagamento,
    tipoCobertura,
    tratamentos,
  ]);

  // Helper functions
  const sortTratamentos = (tratamentos) => {
    const naturalCompare = (a, b) => {
      const ax = [],
        bx = [];
      a.designacao.replace(/(\d+)|(\D+)/g, (_, $1, $2) => {
        ax.push([$1 || Infinity, $2 || ""]);
      });
      b.designacao.replace(/(\d+)|(\D+)/g, (_, $1, $2) => {
        bx.push([$1 || Infinity, $2 || ""]);
      });
      while (ax.length && bx.length) {
        const an = ax.shift(),
          bn = bx.shift();
        const nn = an[0] - bn[0] || an[1].localeCompare(bn[1]);
        if (nn) return nn;
      }
      return ax.length - bx.length;
    };
    return tratamentos.sort(naturalCompare);
  };

  const formatTratamentosAutocomplete = (tratamentos) => {
    return tratamentos.map((t) => ({
      id: t.id,
      label: t.fornecedor?.nome
        ? `${t.designacao} · ${t.fornecedor.nome}`
        : t.designacao,
      categoria: t.categoria.categoria,
    }));
  };

  // Handlers para elementos
  const handleTratamentoChange = (id, tratamento, erro) => {
    setServiceList((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, tratamento, erro } : item,
      ),
    );
    setTouched(true);
  };

  const handleDentesChange = (id, dentes, erro) => {
    setServiceList((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              dentesTratados: dentes,
              erro: erro !== undefined ? erro : item.erro,
            }
          : item,
      ),
    );
    setTouched(true);
  };

  const handleFacesChange = (id, faces, erro) => {
    setServiceList((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, faces, erro: erro !== undefined ? erro : item.erro }
          : item,
      ),
    );
    setTouched(true);
  };

  const handleRemoveService = (id) => {
    setServiceList((prev) =>
      prev.map((item) => (item.id === id ? { ...item, show: false } : item)),
    );
    setTouched(true);
  };

  const handleAddService = () => {
    if (tratamentos.length === 0) return;

    setServiceList((prev) => [
      ...prev,
      {
        id: uuid(),
        tratamento: tratamentos[0],
        dentesTratados: [],
        faces: [],
        show: true,
        erro: "",
      },
    ]);
    setTouched(true);
  };

  const handleDuplicateService = (servico) => {
    const tratamentoCompleto = tratamentos.find(
      (t) => t.id === servico.tratamento?.id,
    );

    setServiceList((prev) => [
      ...prev,
      {
        id: uuid(),
        tratamento: tratamentoCompleto || tratamentos[0],
        dentesTratados: [...servico.dentesTratados],
        faces: [...servico.faces],
        show: true,
        erro: "",
      },
    ]);
    setTouched(true);
  };

  // Handler de cobertura
  const handleInstituicaoChange = (e) => {
    const value = e.target.value;
    if (value === "PARTICULAR") {
      setTipoCobertura("PARTICULAR");
      setInstituicaoSelecionada(null);
      setModalidadePagamento("DIRETO");
    } else {
      const instituicao = instituicoes.find((inst) => inst._id === value);
      setInstituicaoSelecionada(instituicao);
      setModalidadePagamento("DIRETO");
    }
    setCategoriaBeneficiario("");
  };

  // Devolve a lista de dentes que originam procedimentos separados para um
  // serviço. Tratamentos multiDentes geram 1 procedimento com todos os dentes;
  // os restantes geram 1 procedimento por cada dente selecionado.
  const getDentesExpandidos = (service) => {
    const tratamento = service.tratamento;
    if (!tratamento) return [""];

    if (service.dentesTratados && service.dentesTratados.length > 0) {
      if (tratamento.multiDentes) {
        return [service.dentesTratados.join(", ")];
      }
      return [...service.dentesTratados];
    }

    if (tratamento.dentesEspecificos) {
      const dentesEsp = tratamento.dentesEspecificos.split(",");
      if (dentesEsp.length === 1) return [dentesEsp[0]];
    }

    return [""];
  };

  // Atualizar valores
  const updateValores = () => {
    const servicesVisiveis = serviceList.filter((s) => s.show && s.tratamento);

    let tempSubtotal = 0;
    let tempTotalCobertura = 0;
    let cobertos = 0;
    let naoCobertos = 0;

    servicesVisiveis.forEach((service) => {
      const tratamento = service.tratamento;
      // Cada dente de um tratamento não-multiDentes conta como um procedimento.
      const quantidade = getDentesExpandidos(service).length;

      // CORREÇÃO: Verificar apenas instituicaoSelecionada
      if (!instituicaoSelecionada) {
        tempSubtotal += tratamento.preco_particular * quantidade;
        naoCobertos += quantidade;
      } else {
        const cobertura = tratamento.precosPorInstituicao?.find(
          (p) => p.instituicao === instituicaoSelecionada._id,
        );

        if (cobertura?.coberto) {
          tempTotalCobertura += cobertura.comparticipacao * quantidade;
          tempSubtotal +=
            (modalidadePagamento === "DIRETO"
              ? cobertura.preco_beneficiario
              : cobertura.preco_segurado) * quantidade;
          cobertos += quantidade;
        } else {
          tempSubtotal += tratamento.preco_particular * quantidade;
          naoCobertos += quantidade;
        }
      }
    });

    setSubtotal(tempSubtotal);
    setTotalCobertura(tempTotalCobertura);
    setNrTratamentosCobertos(cobertos);
    setNrTratamentosNaoCobertos(naoCobertos);

    // Atualizar tipo de cobertura baseado nos tratamentos
    if (instituicaoSelecionada) {
      if (cobertos === 0 && naoCobertos > 0) {
        setTipoCobertura("PARTICULAR");
        setInstituicaoSelecionada(null);
        setModalidadePagamento("DIRETO");
        setCategoriaBeneficiario("");
      } else if (naoCobertos > 0 && cobertos > 0) {
        setTipoCobertura("MISTO");
      } else if (cobertos > 0) {
        setTipoCobertura("SEGURADO");
      }
    }

    // Calcular subtotal cliente (para reembolso)
    let tempSubtotalCliente = tempSubtotal;
    if (modalidadePagamento === "REEMBOLSO_CLIENTE" && instituicaoSelecionada) {
      tempSubtotalCliente = tempSubtotal - tempTotalCobertura;
    }
    setSubtotalCliente(tempSubtotalCliente);

    // Calcular total com desconto
    calculateTotalWithDesconto(
      desconto,
      tipoDesconto,
      tempSubtotal,
      tempSubtotalCliente,
    );
  };

  const calculateTotalWithDesconto = (desc, tipoDesc, subTo, subToCliente) => {
    let novoTotal;

    if (tipoDesc === "$") {
      novoTotal = subTo - desc;
    } else {
      if (
        modalidadePagamento === "REEMBOLSO_CLIENTE" &&
        instituicaoSelecionada
      ) {
        novoTotal = Math.round(subTo - (desc / 100) * subToCliente);
      } else {
        novoTotal = Math.round(subTo - (desc / 100) * subTo);
      }
    }

    setTotal(novoTotal);

    // Verificar isenção
    const isIsento =
      (tipoDesc === "%" && desc >= 100) || (tipoDesc === "$" && desc >= subTo);
    setOrcamentoIsentoPagamento(isIsento);
  };

  const changeDescontoHandler = (e) => {
    let valor = Number(e.target.value);

    if (valor < 0) valor = 0;
    if (tipoDesconto === "%" && valor > 100) valor = 100;
    if (tipoDesconto === "$" && valor > subtotalCliente)
      valor = subtotalCliente;

    setDesconto(valor);
    calculateTotalWithDesconto(valor, tipoDesconto, subtotal, subtotalCliente);

    // Se tinha parceria aplicada e o valor mudou, invalidar
    if (descontoParceriaAplicado) {
      if (
        tipoDesconto !== "%" ||
        valor !== empresaParceira?.percentagemDesconto
      ) {
        setDescontoParceriaAplicado(false);
      }
    }
  };

  const changeTipoDescontoHandler = (e) => {
    const novoTipo = e.target.value;
    setTipoDesconto(novoTipo);

    let novoDesconto = desconto;
    if (novoTipo === "%" && desconto > 100) {
      novoDesconto = 100;
      setDesconto(100);
    }

    calculateTotalWithDesconto(
      novoDesconto,
      novoTipo,
      subtotal,
      subtotalCliente,
    );

    // Se mudou o tipo, já não é o desconto original da parceria
    if (descontoParceriaAplicado) {
      if (
        novoTipo !== "%" ||
        novoDesconto !== empresaParceira?.percentagemDesconto
      ) {
        setDescontoParceriaAplicado(false);
      }
    }
  };

  // Validação
  const validarOrcamento = () => {
    const servicesVisiveis = serviceList.filter((s) => s.show);

    if (servicesVisiveis.length === 0) {
      return "Adicione pelo menos um tratamento";
    }

    for (const service of servicesVisiveis) {
      if (!service.tratamento) {
        return "Selecione um tratamento para todos os itens";
      }

      if (service.erro) {
        return service.erro;
      }

      // Validar dentes se necessário
      if (
        service.tratamento.aceitaDentes &&
        service.dentesTratados.length === 0
      ) {
        // Verificar se tem dente específico único
        if (service.tratamento.dentesEspecificos) {
          const dentesEsp = service.tratamento.dentesEspecificos.split(",");
          if (dentesEsp.length > 1) {
            return `Selecione o(s) dente(s) para o tratamento ${service.tratamento.designacao}`;
          }
        } else {
          return `Selecione o(s) dente(s) para o tratamento ${service.tratamento.designacao}`;
        }
      }
    }

    return null;
  };

  // Submit
  const submitHandler = async (contaAprovada) => {
    startLoading();
    try {
      const erroValidacao = validarOrcamento();
      if (erroValidacao) {
        toast.error(erroValidacao);
        setIsSubmitting(false);
        stopLoading();
        return;
      }

      const servicesVisiveis = serviceList.filter((s) => s.show);

      // Preparar procedimentos — um procedimento por cada dente (exceto
      // tratamentos multiDentes, que ficam num único procedimento). Os valores
      // são unitários (por procedimento/dente).
      const procedimentos = [];
      servicesVisiveis.forEach((service) => {
        const tratamento = service.tratamento;
        let valorCobertura = 0;
        let preco_beneficiario = 0;
        let valorCliente = 0;
        let precoSegurado = 0;

        if (tipoCobertura === "PARTICULAR" || !instituicaoSelecionada) {
          valorCliente = tratamento.preco_particular;
        } else {
          const cobertura = tratamento.precosPorInstituicao?.find(
            (p) => p.instituicao === instituicaoSelecionada._id,
          );

          if (cobertura?.coberto) {
            precoSegurado = cobertura.preco_segurado;
            if (modalidadePagamento === "DIRETO") {
              valorCobertura = cobertura.comparticipacao;
              valorCliente = cobertura.preco_beneficiario;
              preco_beneficiario = cobertura.preco_beneficiario;
            } else {
              valorCobertura = cobertura.comparticipacao;
              valorCliente = cobertura.preco_segurado;
              preco_beneficiario = cobertura.preco_beneficiario;
            }
          } else {
            valorCliente = tratamento.preco_particular;
          }
        }

        // Um procedimento por dente (ou um único, para multiDentes/sem dente)
        const dentesProcedimento = getDentesExpandidos(service);

        dentesProcedimento.forEach((denteStr, idx) => {
          procedimentos.push({
            id: `${service.id}-${idx}`,
            tratamento: tratamento.id,
            fornecedor:
              tratamento.fornecedor?._id || tratamento.fornecedor || null,
            dente: denteStr,
            faces: service.faces,
            preco_segurado: precoSegurado,
            valor_cobertura: valorCobertura,
            valor_cliente: valorCliente,
            preco_beneficiario: preco_beneficiario,
          });
        });
      });

      // FormData
      const formData = new FormData();
      formData.append("cliente", clienteId);
      formData.append("medico_responsavel", medicoResponsavel);
      formData.append("clinica", clinica.clinica._id);
      formData.append("tipoCobertura", tipoCobertura);

      if (instituicaoSelecionada) {
        formData.append("instituicaoCobertura", instituicaoSelecionada._id);
        formData.append("modalidadePagamento", modalidadePagamento);
        if (instituicaoSelecionada.nome === "INPS") {
          formData.append("categoriaBeneficiario", categoriaBeneficiario);
        }
      }

      formData.append(
        "beneficio",
        tipoCobertura === "PARTICULAR"
          ? "Particular"
          : instituicaoSelecionada.nome,
      );

      formData.append("procedimentos", JSON.stringify(procedimentos));
      formData.append("total", total);
      formData.append("subtotal", subtotal);
      formData.append("subtotalCliente", subtotalCliente);

      const descontoEmEscudos =
        tipoDesconto === "$"
          ? desconto
          : modalidadePagamento === "REEMBOLSO_CLIENTE"
            ? Math.round((subtotalCliente * desconto) / 100)
            : Math.round((subtotal * desconto) / 100);

      formData.append("desconto", descontoEmEscudos);

      if (descontoParceriaAplicado && empresaParceira) {
        formData.append(
          "descontoParceria",
          JSON.stringify({
            empresaParceira: empresaParceira._id,
            nomeEmpresa: empresaParceira.nome,
            percentagemAplicada: empresaParceira.percentagemDesconto,
            valorDesconto: descontoEmEscudos,
            dataAplicacao: new Date().toISOString(),
          }),
        );
      }

      formData.append("tipoDesconto", tipoDesconto);

      if (tipoDesconto === "%") {
        formData.append("percentagemDescontoOriginal", desconto);
      }

      formData.append(
        "diasValidadeParcela",
        clinica.clinica?.configuracoes?.configuracoes?.validadeParcela
          ?.validade || 15,
      );

      formData.append("observacoes", observacoes || "");
      formData.append("criadoPor", auth.userId);
      formData.append("contaAprovada", contaAprovada);
      formData.append("terminado", false);
      formData.append("orcamentoIsentoPagamento", orcamentoIsentoPagamento);
      formData.append("caixaId", caixas[0]?._id);

      // Chamar API de orçamento direto
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/orcamentos/direto`,
        "POST",
        formData,
        {
          Authorization: "Bearer " + auth.token,
        },
      );

      toast.success(
        `Orçamento ${
          orcamentoIsentoPagamento ? "isento de pagamento " : ""
        }criado com sucesso!`,
      );
      navigate(`../../../clientes/${clienteId}-orcamento`);
    } catch (err) {
      console.error("Erro ao criar orçamento:", err);
      toast.error("Erro ao criar orçamento");
    } finally {
      setIsSubmitting(false);
      stopLoading();
    }
  };

  // Submit para 2 orçamentos (MISTO)
  const submitHandlerDoisOrcamentos = async (descSegurado, descParticular) => {
    startLoading();
    try {
      const erroValidacao = validarOrcamento();
      if (erroValidacao) {
        toast.error(erroValidacao);
        setIsSubmitting(false);
        stopLoading();
        return;
      }

      const servicesVisiveis = serviceList.filter((s) => s.show);

      // Separar procedimentos
      const procedimentosCobertos = [];
      const procedimentosNaoCobertos = [];

      servicesVisiveis.forEach((service) => {
        const tratamento = service.tratamento;
        const cobertura = tratamento.precosPorInstituicao?.find(
          (p) => p.instituicao === instituicaoSelecionada._id,
        );

        // Um procedimento por dente (exceto multiDentes/sem dente)
        const dentesProcedimento = getDentesExpandidos(service);

        dentesProcedimento.forEach((denteStr, idx) => {
          const procedimento = {
            id: `${service.id}-${idx}`,
            tratamento: tratamento.id,
            fornecedor:
              tratamento.fornecedor?._id || tratamento.fornecedor || null,
            dente: denteStr,
            faces: service.faces,
          };

          if (cobertura?.coberto) {
            procedimento.preco_segurado = cobertura.preco_segurado;
            procedimento.valor_cobertura = cobertura.comparticipacao;
            procedimento.valor_cliente =
              modalidadePagamento === "DIRETO"
                ? cobertura.preco_beneficiario
                : cobertura.preco_segurado;
            procedimento.preco_beneficiario = cobertura.preco_beneficiario;
            procedimentosCobertos.push(procedimento);
          } else {
            procedimento.preco_segurado = 0;
            procedimento.valor_cobertura = 0;
            procedimento.valor_cliente = tratamento.preco_particular;
            procedimento.preco_beneficiario = 0;
            procedimentosNaoCobertos.push(procedimento);
          }
        });
      });

      // Calcular totais
      const subtotalCobertos = procedimentosCobertos.reduce(
        (acc, p) => acc + p.valor_cliente,
        0,
      );
      const subtotalNaoCobertos = procedimentosNaoCobertos.reduce(
        (acc, p) => acc + p.valor_cliente,
        0,
      );

      const totalCobertos = subtotalCobertos - descSegurado;
      const totalNaoCobertos = subtotalNaoCobertos - descParticular;

      // Dados comuns
      const diasValidadeParcela =
        clinica.clinica?.configuracoes?.configuracoes?.validadeParcela
          ?.validade || 15;

      // 1º Orçamento - SEGURADO
      const formData1 = new FormData();
      formData1.append("cliente", clienteId);
      formData1.append("medico_responsavel", medicoResponsavel);
      formData1.append("clinica", clinica.clinica._id);
      formData1.append("tipoCobertura", "SEGURADO");
      formData1.append("instituicaoCobertura", instituicaoSelecionada._id);
      formData1.append("modalidadePagamento", modalidadePagamento);
      formData1.append("beneficio", instituicaoSelecionada.nome);
      if (instituicaoSelecionada.nome === "INPS") {
        formData1.append("categoriaBeneficiario", categoriaBeneficiario);
      }
      formData1.append("procedimentos", JSON.stringify(procedimentosCobertos));
      formData1.append("total", totalCobertos);
      formData1.append("subtotal", subtotalCobertos);
      formData1.append("subtotalCliente", subtotalCobertos);
      formData1.append("desconto", descSegurado);
      formData1.append("tipoDesconto", tipoDesconto);
      if (tipoDesconto === "%" && descSegurado > 0) {
        const percentagemReal = (descSegurado / subtotalCobertos) * 100;
        formData1.append(
          "percentagemDescontoOriginal",
          percentagemReal.toFixed(2),
        );
      }
      formData1.append("diasValidadeParcela", diasValidadeParcela);
      formData1.append("observacoes", observacoes || "");
      formData1.append("criadoPor", auth.userId);
      formData1.append("contaAprovada", false);
      formData1.append("terminado", false);
      formData1.append(
        "orcamentoIsentoPagamento",
        descSegurado >= subtotalCobertos,
      );
      formData1.append("caixaId", caixas[0]?._id);

      if (descontoParceriaAplicado && empresaParceira && descSegurado > 0) {
        const descontoEsperado = Math.round(
          (subtotalCobertos * empresaParceira.percentagemDesconto) / 100,
        );
        if (Math.abs(descSegurado - descontoEsperado) <= 10) {
          formData1.append(
            "descontoParceria",
            JSON.stringify({
              empresaParceira: empresaParceira._id,
              nomeEmpresa: empresaParceira.nome,
              percentagemAplicada: empresaParceira.percentagemDesconto,
              valorDesconto: descSegurado,
              dataAplicacao: new Date().toISOString(),
            }),
          );
        }
      }

      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/orcamentos/direto`,
        "POST",
        formData1,
        { Authorization: "Bearer " + auth.token },
      );

      // 2º Orçamento - PARTICULAR
      const formData2 = new FormData();
      formData2.append("cliente", clienteId);
      formData2.append("medico_responsavel", medicoResponsavel);
      formData2.append("clinica", clinica.clinica._id);
      formData2.append("tipoCobertura", "PARTICULAR");
      formData2.append("beneficio", "Particular");
      formData2.append(
        "procedimentos",
        JSON.stringify(procedimentosNaoCobertos),
      );
      formData2.append("total", totalNaoCobertos);
      formData2.append("subtotal", subtotalNaoCobertos);
      formData2.append("subtotalCliente", subtotalNaoCobertos);
      formData2.append("desconto", descParticular);
      formData2.append("tipoDesconto", tipoDesconto);
      if (tipoDesconto === "%" && descParticular > 0) {
        const percentagemReal = (descParticular / subtotalNaoCobertos) * 100;
        formData2.append(
          "percentagemDescontoOriginal",
          percentagemReal.toFixed(2),
        );
      }
      formData2.append("diasValidadeParcela", diasValidadeParcela);
      formData2.append("observacoes", observacoes || "");
      formData2.append("criadoPor", auth.userId);
      formData2.append("contaAprovada", false);
      formData2.append("terminado", false);
      formData2.append(
        "orcamentoIsentoPagamento",
        descParticular >= subtotalNaoCobertos,
      );
      formData2.append("caixaId", caixas[0]?._id);

      if (descontoParceriaAplicado && empresaParceira && descParticular > 0) {
        const descontoEsperado = Math.round(
          (subtotalNaoCobertos * empresaParceira.percentagemDesconto) / 100,
        );
        if (Math.abs(descParticular - descontoEsperado) <= 10) {
          formData2.append(
            "descontoParceria",
            JSON.stringify({
              empresaParceira: empresaParceira._id,
              nomeEmpresa: empresaParceira.nome,
              percentagemAplicada: empresaParceira.percentagemDesconto,
              valorDesconto: descParticular,
              dataAplicacao: new Date().toISOString(),
            }),
          );
        }
      }

      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/orcamentos/direto`,
        "POST",
        formData2,
        { Authorization: "Bearer " + auth.token },
      );

      toast.success("2 Orçamentos criados com sucesso!");
      navigate(`../../../clientes/${clienteId}-orcamento`);
    } catch (err) {
      console.error("Erro ao criar orçamentos:", err);
      toast.error("Erro ao criar orçamentos");
    } finally {
      setIsSubmitting(false);
      stopLoading();
    }
  };

  // Pre-submit handlers
  const preSubmitHandler = (aprovar) => {
    if (
      (auth.role === "Secretária(o)" || auth.role === "Assistente") &&
      !medicoResponsavel
    ) {
      toast.error("Selecione um médico para continuar!");
      return;
    }

    if (!isSubmitting) {
      setIsSubmitting(true);
      submitHandler(aprovar);
    }
  };

  const preSubmitHandlerDoisOrcamentos = () => {
    if (
      (auth.role === "Secretária(o)" || auth.role === "Assistente") &&
      !medicoResponsavel
    ) {
      toast.error("Selecione um médico para continuar!");
      return;
    }

    if (!isSubmitting) {
      const servicesVisiveis = serviceList.filter((s) => s.show);
      if (servicesVisiveis.length === 0) {
        toast.error("Adicione pelo menos um tratamento");
        return;
      }

      if (desconto > 0) {
        const { subtotalCobertos, subtotalNaoCobertos } =
          calcularTotaisOrcamentos();
        const descontoEmEscudos =
          tipoDesconto === "$"
            ? desconto
            : Math.round(
                ((subtotalCobertos + subtotalNaoCobertos) * desconto) / 100,
              );

        setDescontoParticular(descontoEmEscudos);
        setDescontoSegurado(0);
        setOpcaoRapidaSelecionada("particular");
        setModalDescontoAberta(true);
      } else {
        setIsSubmitting(true);
        submitHandlerDoisOrcamentos(0, 0);
      }
    }
  };

  const calcularTotaisOrcamentos = () => {
    let subtotalCobertos = 0;
    let subtotalNaoCobertos = 0;

    serviceList
      .filter((s) => s.show && s.tratamento)
      .forEach((service) => {
        const cobertura = service.tratamento?.precosPorInstituicao?.find(
          (p) => p.instituicao === instituicaoSelecionada?._id,
        );

        // Cada dente de um tratamento não-multiDentes conta como procedimento.
        const quantidade = getDentesExpandidos(service).length;

        if (cobertura?.coberto) {
          subtotalCobertos +=
            (modalidadePagamento === "DIRETO"
              ? cobertura.preco_beneficiario
              : cobertura.preco_segurado) * quantidade;
        } else {
          subtotalNaoCobertos +=
            (service.tratamento?.preco_particular || 0) * quantidade;
        }
      });

    return { subtotalCobertos, subtotalNaoCobertos };
  };

  const aplicarDescontoRapido = (opcao) => {
    const { subtotalCobertos, subtotalNaoCobertos } =
      calcularTotaisOrcamentos();
    const descontoEmEscudos =
      tipoDesconto === "$"
        ? desconto
        : Math.round(
            ((subtotalCobertos + subtotalNaoCobertos) * desconto) / 100,
          );

    setOpcaoRapidaSelecionada(opcao);

    switch (opcao) {
      case "particular":
        setDescontoParticular(descontoEmEscudos);
        setDescontoSegurado(0);
        break;
      case "segurado":
        setDescontoSegurado(descontoEmEscudos);
        setDescontoParticular(0);
        break;
      case "proporcional":
        const totalGeral = subtotalCobertos + subtotalNaoCobertos;
        const descSeg = Math.round(
          (descontoEmEscudos * subtotalCobertos) / totalGeral,
        );
        const descPart = Math.round(
          (descontoEmEscudos * subtotalNaoCobertos) / totalGeral,
        );
        setDescontoSegurado(descSeg);
        setDescontoParticular(descPart);
        break;
      case "limpar":
        setDescontoSegurado(0);
        setDescontoParticular(0);
        break;
      default:
        break;
    }
  };

  // Shortcuts
  const precisaPopupDentes = (shortcut) => {
    return shortcut.tratamentos.some((item) => {
      const tratamentoId =
        item.tratamento?.id ||
        item.tratamento?._id ||
        item.tratamento ||
        item.id ||
        item._id ||
        item;
      const tratamentoCompleto = tratamentos.find((t) => t.id === tratamentoId);

      if (!tratamentoCompleto) return false;
      if (!tratamentoCompleto.aceitaDentes) return false;

      if (tratamentoCompleto.dentesEspecificos) {
        const dentesEspecificosArray =
          tratamentoCompleto.dentesEspecificos.split(",");
        if (dentesEspecificosArray.length === 1) return false;
        return true;
      }

      return true;
    });
  };

  const iniciarAplicacaoShortcut = (shortcut) => {
    if (precisaPopupDentes(shortcut)) {
      setShortcutAtual(shortcut);
      setDentesSelecionadosShortcut([]);
      setShowDentesShortcutModal(true);
    } else {
      aplicarShortcutHandler(shortcut, []);
    }
  };

  const aplicarShortcutHandler = (shortcut, dentesSelecionados = []) => {
    const novosTratamentos = [];
    const tratamentosAgrupados = new Map();

    shortcut.tratamentos.forEach((item) => {
      const tratamentoId =
        item.tratamento?.id ||
        item.tratamento?._id ||
        item.tratamento ||
        item.id ||
        item._id ||
        item;
      const quantidade = item.quantidade || 1;

      const tratamentoCompleto = tratamentos.find((t) => t.id === tratamentoId);
      if (!tratamentoCompleto) return;

      let dentesTratados = [];
      if (tratamentoCompleto.aceitaDentes) {
        if (tratamentoCompleto.dentesEspecificos) {
          const dentesEspecificosArray = tratamentoCompleto.dentesEspecificos
            .split(",")
            .map((d) => d.trim());

          if (dentesEspecificosArray.length === 1) {
            dentesTratados = dentesEspecificosArray;
          } else {
            dentesTratados = dentesSelecionados.filter((dente) =>
              dentesEspecificosArray.includes(dente),
            );
          }
        } else {
          dentesTratados = [...dentesSelecionados];
        }
      }

      if (!tratamentosAgrupados.has(tratamentoId)) {
        tratamentosAgrupados.set(tratamentoId, {
          tratamento: tratamentoCompleto,
          dentesTratados,
          quantidade,
        });
      } else {
        const existing = tratamentosAgrupados.get(tratamentoId);
        existing.quantidade += quantidade;
      }
    });

    tratamentosAgrupados.forEach((info) => {
      const { tratamento, dentesTratados, quantidade } = info;

      for (let i = 0; i < quantidade; i++) {
        novosTratamentos.push({
          id: uuid(),
          tratamento: tratamento,
          dentesTratados: [...dentesTratados],
          faces: [],
          show: true,
          erro: "",
        });
      }
    });

    if (!touched) {
      setServiceList(novosTratamentos);
    } else {
      setServiceList((current) => [...current, ...novosTratamentos]);
    }

    setTouched(true);
    toast.success(
      `Atalho "${shortcut.nome}" aplicado: ${tratamentosAgrupados.size} tipo(s) de tratamento`,
    );
  };

  // Cancel handler
  const handleCancelClick = () => {
    if (serviceList.filter((s) => s.show).length > 1 || touched) {
      setShowCancelDialog(true);
    } else {
      navigate(`../../../clientes/${clienteId}-orcamento`);
    }
  };

  return (
    <>
      <div className="new">
        <Sidebar />
        <div className="newContainer">
          {cliente && (
            <Navbar
              title={isDuplicacao ? "Duplicar Orçamento" : "Orçamento Direto"}
              icon={ArticleOutlinedIcon}
              paths={[
                {
                  nome: cliente.nome,
                  link: `../../../clientes/${cliente.id}-orcamento`,
                },
                {
                  nome: isDuplicacao
                    ? "Duplicar Orçamento"
                    : "Novo Orçamento Direto",
                },
              ]}
            />
          )}

          {tratamentos.length > 0 && cliente && (
            <div className="bottom-orcamento caixa">
              {/* A duplicar, a decisão de ser direto já foi tomada no orçamento
                  de origem — a nota só teria a dizer o que já está feito. */}
              {!isDuplicacao && (
                <NotaInformativa
                  titulo="Quando se faz um orçamento direto"
                  style={{ margin: "0 0 20px" }}
                >
                  <ul style={{ margin: 0, paddingLeft: "18px" }}>
                    <li>
                      Um orçamento <strong>direto</strong> é um orçamento que{" "}
                      <strong>não precisa de uma avaliação</strong>.
                    </li>
                    <li>
                      Se já existe uma avaliação com os tratamentos que quer
                      orçamentar, <strong>não o crie aqui</strong>: faça o
                      orçamento <strong>a partir dessa avaliação</strong>, para
                      o orçamento ficar ligado ao que foi observado.
                    </li>
                  </ul>
                </NotaInformativa>
              )}

              {/* Seletor de Médico */}
              {(auth.role === "Secretária(o)" ||
                auth.role === "Assistente") && (
                <div className="avaliacaoPickMedcoContainer form-input">
                  <span className="avaliacaoPickMedcoContainer__medicoHeader">
                    Médico responsável
                  </span>
                  <select
                    id="select_medicoResponsavel"
                    name="medicoResponsavel"
                    value={medicoResponsavel}
                    onChange={(e) => setMedicoResponsavel(e.target.value)}
                    className="input"
                  >
                    <option value="">Selecione um médico</option>
                    {auth.usuariosAssociados.map((med, index) => (
                      <option value={med.id} key={index}>
                        {med.nome}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Header de cobertura */}
              <div className="pOrcamento-header">
                <FormControl fullWidth size="small" sx={{ maxWidth: "300px" }}>
                  <InputLabel id="label-cobertura">Cobertura</InputLabel>
                  <Select
                    labelId="label-cobertura"
                    id="select_cobertura"
                    value={instituicaoSelecionada?._id || "PARTICULAR"}
                    label="Cobertura"
                    onChange={handleInstituicaoChange}
                  >
                    <MenuItem value="PARTICULAR">Particular</MenuItem>
                    {instituicoes.map((inst) => (
                      <MenuItem value={inst._id} key={inst._id}>
                        {inst.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {instituicaoSelecionada?.nome === "INPS" && (
                  <FormControl
                    fullWidth
                    size="small"
                    sx={{ maxWidth: "300px" }}
                  >
                    <InputLabel id="label-categoria">
                      Categoria Beneficiário
                    </InputLabel>
                    <Select
                      labelId="label-categoria"
                      value={categoriaBeneficiario}
                      label="Categoria"
                      onChange={(e) => setCategoriaBeneficiario(e.target.value)}
                    >
                      <MenuItem value="">Selecione uma categoria</MenuItem>
                      <MenuItem value="TCP">Trabalhador Conta Própria</MenuItem>
                      <MenuItem value="TCO">Trabalhador Conta Outrem</MenuItem>
                      <MenuItem value="PA">Função Pública</MenuItem>
                      <MenuItem value="MBCSS">
                        Migrantes - Beneficiários Convenções
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}

                {instituicaoSelecionada && (
                  <FormControl
                    fullWidth
                    size="small"
                    sx={{ maxWidth: "300px" }}
                  >
                    <InputLabel id="label-modalidade">Modalidade</InputLabel>
                    <Select
                      labelId="label-modalidade"
                      value={modalidadePagamento}
                      label="Modalidade"
                      onChange={(e) => setModalidadePagamento(e.target.value)}
                    >
                      <MenuItem value="DIRETO">Direto</MenuItem>
                      <MenuItem value="REEMBOLSO_CLIENTE">Reembolso</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </div>

              {/* Alerta de Parceria */}
              {parceriaValida && empresaParceira && (
                <div
                  style={{
                    backgroundColor: "#e8f5e9",
                    border: "1px solid #4caf50",
                    borderRadius: "8px",
                    padding: "15px 20px",
                    marginBottom: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "15px",
                    }}
                  >
                    <BusinessIcon
                      style={{ color: "#4caf50", fontSize: "28px" }}
                    />
                    <div>
                      <div
                        style={{
                          fontWeight: "600",
                          color: "#2e7d32",
                          fontSize: "15px",
                        }}
                      >
                        Cliente de Empresa Parceira
                      </div>
                      <div style={{ color: "#388e3c", fontSize: "14px" }}>
                        <strong>{empresaParceira.nome}</strong> - Desconto de{" "}
                        <strong>{empresaParceira.percentagemDesconto}%</strong>{" "}
                        disponível
                      </div>
                    </div>
                  </div>

                  {!descontoParceriaAplicado ? (
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      onClick={() => {
                        setDesconto(empresaParceira.percentagemDesconto);
                        setTipoDesconto("%");
                        setDescontoParceriaAplicado(true);
                        calculateTotalWithDesconto(
                          empresaParceira.percentagemDesconto,
                          "%",
                          subtotal,
                          subtotalCliente,
                        );
                        toast.success(
                          `Desconto de ${empresaParceira.percentagemDesconto}% aplicado!`,
                        );
                      }}
                    >
                      Aplicar Desconto
                    </Button>
                  ) : (
                    <div
                      style={{
                        backgroundColor: "#4caf50",
                        color: "white",
                        padding: "6px 16px",
                        borderRadius: "4px",
                        fontSize: "13px",
                        fontWeight: "500",
                      }}
                    >
                      ✓ Desconto Aplicado
                    </div>
                  )}
                </div>
              )}

              {/* Header da tabela */}
              <div
                className={`orcamento__item orcamento__item--headerDiv ${
                  tipoCobertura === "PARTICULAR"
                    ? "orcamento__item--particular"
                    : ""
                }`}
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    tipoCobertura === "PARTICULAR"
                      ? "2fr 1fr 1fr 1fr 100px"
                      : "2fr 1fr 1fr 1fr 1fr 1fr 100px",
                  marginBottom: "15px",
                }}
              >
                <span className="orcamento__item--header">Tratamento</span>
                <span className="orcamento__item--header">Dente</span>
                <span className="orcamento__item--header">Faces</span>
                <span className="orcamento__item--header">Val. Unit.</span>
                {tipoCobertura !== "PARTICULAR" && instituicaoSelecionada && (
                  <>
                    <span className="orcamento__item--header">
                      {instituicaoSelecionada.nome}
                    </span>
                    <span className="orcamento__item--header">
                      Beneficiário
                    </span>
                  </>
                )}
                <span className="orcamento__item--header">Ações</span>
              </div>

              {/* Lista de Tratamentos */}
              {serviceList.map(
                (service) =>
                  service.show && (
                    <ElementoOrcamentoDireto
                      key={service.id}
                      id={service.id}
                      tratamentos={tratamentos}
                      tratamentosAutocomplete={tratamentosAutocomplete}
                      dentes={DENTES}
                      show={service.show}
                      podeRemover={serviceList.filter((s) => s.show).length > 1}
                      tratamentoInicial={service.tratamento?.id}
                      dentesTratadosIniciais={service.dentesTratados}
                      facesIniciais={service.faces}
                      tipoCobertura={tipoCobertura}
                      instituicaoSelecionada={instituicaoSelecionada}
                      modalidadePagamento={modalidadePagamento}
                      onTratamentoChange={handleTratamentoChange}
                      onDentesChange={handleDentesChange}
                      onFacesChange={handleFacesChange}
                      onRemove={handleRemoveService}
                      onDuplicate={handleDuplicateService}
                    />
                  ),
              )}

              {/* Botão Adicionar */}
              <button
                className="add-btn"
                onClick={handleAddService}
                style={{ marginLeft: "10px", marginTop: "15px" }}
              >
                +
              </button>

              {/* Shortcuts */}
              {shortcuts && shortcuts.length > 0 && (
                <div
                  style={{
                    marginLeft: "10px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.9em",
                      color: "#666",
                      marginBottom: "5px",
                    }}
                  >
                    Atalhos:
                  </div>
                  <div
                    style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}
                  >
                    {shortcuts.map((shortcut) => (
                      <button
                        key={shortcut.id}
                        className="blue-button"
                        onClick={() => iniciarAplicacaoShortcut(shortcut)}
                        style={{ padding: "6px 12px", fontSize: "0.9em" }}
                      >
                        {shortcut.nome}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Área de Pagamento */}
              <span className="orcamento__areaPagamento__titulo">
                Pagamento
              </span>
              <div className="orcamento__areaPagamento__container">
                <div className="orcamento__areaPagamento__container--1">
                  <span className="orcamento__areaPagamento__tituloDesconto">
                    Desconto
                  </span>
                  <div className="orcamento__areaPagamento__desconto">
                    <select
                      onChange={changeTipoDescontoHandler}
                      value={tipoDesconto}
                    >
                      <option value="$">$00</option>
                      <option value="%">%</option>
                    </select>
                    <input
                      type="number"
                      min={0}
                      value={desconto}
                      onChange={changeDescontoHandler}
                      onKeyDown={(e) => e.key === "." && e.preventDefault()}
                      step={1}
                    />
                  </div>

                  <span className="orcamento__areaPagamento__tituloDesconto">
                    Observação
                  </span>
                  <textarea
                    cols="30"
                    rows="7"
                    value={observacoes}
                    onChange={(e) => setObservacoes(e.target.value)}
                  />
                </div>

                <div className="orcamento__areaPagamento__container--3"></div>

                <div className="orcamento__areaPagamento__container--2">
                  <span className="orcamento__areaPagamento__container--2__titulo">
                    Detalhes sobre o pagamento
                  </span>
                  <div className="orcamento__areaPagamento__container--2__line">
                    <span>Subtotal:</span>
                    <span>
                      {Intl.NumberFormat("pt-BR").format(subtotal)}$00
                    </span>
                  </div>

                  {modalidadePagamento === "REEMBOLSO_CLIENTE" &&
                    instituicaoSelecionada && (
                      <>
                        <div className="orcamento__areaPagamento__container--2__line">
                          <span>Total Reembolso:</span>
                          <span>
                            {Intl.NumberFormat("pt-BR").format(totalCobertura)}
                            $00
                          </span>
                        </div>
                        <div className="orcamento__areaPagamento__container--2__line">
                          <span>Total Beneficiário:</span>
                          <span>
                            {Intl.NumberFormat("pt-BR").format(subtotalCliente)}
                            $00
                          </span>
                        </div>
                      </>
                    )}
                  {tipoCobertura === "MISTO" && instituicaoSelecionada && (
                    <>
                      {modalidadePagamento !== "REEMBOLSO_CLIENTE" && (
                        <div className="orcamento__areaPagamento__container--2__line">
                          <span>Total {instituicaoSelecionada.nome}:</span>
                          <span>
                            {Intl.NumberFormat("pt-BR").format(
                              serviceList
                                .filter((s) => s.show && s.tratamento)
                                .filter(
                                  (s) =>
                                    s.tratamento.precosPorInstituicao?.find(
                                      (p) =>
                                        p.instituicao ===
                                        instituicaoSelecionada._id,
                                    )?.coberto,
                                )
                                .reduce((acc, s) => {
                                  const cob =
                                    s.tratamento.precosPorInstituicao.find(
                                      (p) =>
                                        p.instituicao ===
                                        instituicaoSelecionada._id,
                                    );
                                  return (
                                    acc +
                                    (modalidadePagamento === "DIRETO"
                                      ? cob.preco_beneficiario
                                      : cob.preco_segurado)
                                  );
                                }, 0),
                            )}
                            $00
                          </span>
                        </div>
                      )}
                      <div className="orcamento__areaPagamento__container--2__line">
                        <span>Total Particular:</span>
                        <span>
                          {Intl.NumberFormat("pt-BR").format(
                            serviceList
                              .filter((s) => s.show && s.tratamento)
                              .filter(
                                (s) =>
                                  !s.tratamento.precosPorInstituicao?.find(
                                    (p) =>
                                      p.instituicao ===
                                      instituicaoSelecionada._id,
                                  )?.coberto,
                              )
                              .reduce(
                                (acc, s) => acc + s.tratamento.preco_particular,
                                0,
                              ),
                          )}
                          $00
                        </span>
                      </div>
                    </>
                  )}

                  <div className="orcamento__areaPagamento__container--2__line">
                    <span>Desconto:</span>
                    <span>
                      {Intl.NumberFormat("pt-BR").format(
                        tipoDesconto === "$"
                          ? desconto
                          : modalidadePagamento === "REEMBOLSO_CLIENTE"
                            ? Math.round((subtotalCliente * desconto) / 100)
                            : Math.round((subtotal * desconto) / 100),
                      )}
                      $00
                    </span>
                  </div>

                  <div className="orcamento__areaPagamento__container--2__line">
                    <span className="orcamento__areaPagamento__container--2__line__title-big">
                      Total
                    </span>
                    <span>{Intl.NumberFormat("pt-BR").format(total)}$00</span>
                  </div>
                </div>
              </div>

              {/* Botões de Ação */}
              <div className="botoes">
                <button className="cancel-btn" onClick={handleCancelClick}>
                  Cancelar
                </button>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="blue-button"
                  onClick={() => preSubmitHandler(false)}
                >
                  {isSubmitting ? "Criando..." : "Criar"}
                </button>

                {nrTratamentosNaoCobertos > 0 && nrTratamentosCobertos > 0 && (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="blue-button"
                    onClick={preSubmitHandlerDoisOrcamentos}
                  >
                    {isSubmitting ? "Criando..." : "Criar 2 Orçamentos"}
                  </button>
                )}

                {tipoCobertura === "PARTICULAR" && (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="blue-button"
                    onClick={() => preSubmitHandler(true)}
                  >
                    {isSubmitting ? "Criando..." : "Criar e Aprovar"}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dialog de Cancelamento */}
      <Dialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
      >
        <DialogTitle>Cancelar orçamento?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja cancelar? Todos os dados não guardados serão
            perdidos.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <button
            className="cancel-btn"
            onClick={() => setShowCancelDialog(false)}
          >
            Voltar
          </button>
          <button
            className="blue-button"
            onClick={() => navigate(`../../../clientes/${clienteId}-orcamento`)}
            autoFocus
          >
            Confirmar
          </button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Seleção de Dentes para Shortcut */}
      <Dialog
        open={showDentesShortcutModal}
        onClose={() => {
          setShowDentesShortcutModal(false);
          setShortcutAtual(null);
          setDentesSelecionadosShortcut([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Selecionar dente(s) para: {shortcutAtual?.nome}
        </DialogTitle>
        <DialogContent>
          <DialogContentText style={{ marginBottom: "20px" }}>
            Selecione o(s) dente(s) que serão aplicados aos tratamentos deste
            atalho.
          </DialogContentText>
          <FormControl fullWidth>
            <InputLabel>
              Dentes{" "}
              {dentesSelecionadosShortcut.length > 0
                ? `(${dentesSelecionadosShortcut.length})`
                : ""}
            </InputLabel>
            <Select
              multiple
              value={dentesSelecionadosShortcut}
              onChange={(e) => setDentesSelecionadosShortcut(e.target.value)}
              input={<OutlinedInput label="Dentes" />}
              renderValue={(selected) => selected.join(", ")}
              MenuProps={{
                PaperProps: { style: { maxHeight: 48 * 4.5 + 8, width: 250 } },
              }}
            >
              {DENTES.map((dente) => (
                <MenuItem key={dente} value={dente}>
                  <Checkbox
                    checked={dentesSelecionadosShortcut.indexOf(dente) > -1}
                  />
                  <ListItemText primary={dente} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <button
            className="cancel-btn"
            onClick={() => {
              setShowDentesShortcutModal(false);
              setShortcutAtual(null);
              setDentesSelecionadosShortcut([]);
            }}
          >
            Cancelar
          </button>
          <button
            className="blue-button"
            onClick={() => {
              if (dentesSelecionadosShortcut.length === 0) {
                toast.error("Selecione pelo menos um dente");
                return;
              }
              aplicarShortcutHandler(shortcutAtual, dentesSelecionadosShortcut);
              setShowDentesShortcutModal(false);
              setShortcutAtual(null);
              setDentesSelecionadosShortcut([]);
            }}
            autoFocus
          >
            Aplicar
          </button>
        </DialogActions>
      </Dialog>

      {/* Modal de Distribuição de Desconto (2 Orçamentos) */}
      {modalDescontoAberta &&
        (() => {
          const { subtotalCobertos, subtotalNaoCobertos } =
            calcularTotaisOrcamentos();
          const totalGeral = subtotalCobertos + subtotalNaoCobertos;
          const descontoEmEscudos =
            tipoDesconto === "$"
              ? desconto
              : Math.round((totalGeral * desconto) / 100);
          const totalDistribuido = descontoSegurado + descontoParticular;
          const diferenca = descontoEmEscudos - totalDistribuido;
          const erroSegurado = descontoSegurado > subtotalCobertos;
          const erroParticular = descontoParticular > subtotalNaoCobertos;
          const avisoExcesso = totalDistribuido > descontoEmEscudos;
          const totalFinalSegurado = subtotalCobertos - descontoSegurado;
          const totalFinalParticular = subtotalNaoCobertos - descontoParticular;
          const temErro = erroSegurado || erroParticular;

          return (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0,0,0,0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 9999,
              }}
              onClick={() => {
                setModalDescontoAberta(false);
                setOpcaoRapidaSelecionada(null);
              }}
            >
              <div
                style={{
                  backgroundColor: "white",
                  padding: "30px",
                  borderRadius: "8px",
                  maxWidth: "600px",
                  width: "90%",
                  maxHeight: "90vh",
                  overflow: "auto",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <h3 style={{ marginBottom: "20px", marginTop: 0 }}>
                  Distribuir Desconto de{" "}
                  {Intl.NumberFormat("pt-BR").format(descontoEmEscudos)}$00
                </h3>

                {/* Info dos orçamentos */}
                <div
                  style={{
                    backgroundColor: "#f5f5f5",
                    padding: "15px",
                    borderRadius: "6px",
                    marginBottom: "20px",
                    fontSize: "14px",
                  }}
                >
                  <div style={{ marginBottom: "8px" }}>
                    <strong>Orçamento {instituicaoSelecionada?.nome}:</strong>{" "}
                    {Intl.NumberFormat("pt-BR").format(subtotalCobertos)}$00
                  </div>
                  <div style={{ marginBottom: "8px" }}>
                    <strong>Orçamento Particular:</strong>{" "}
                    {Intl.NumberFormat("pt-BR").format(subtotalNaoCobertos)}$00
                  </div>
                  <div>
                    <strong>Total:</strong>{" "}
                    {Intl.NumberFormat("pt-BR").format(totalGeral)}$00
                  </div>
                </div>

                {/* Botões rápidos */}
                <div style={{ marginBottom: "25px" }}>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      marginBottom: "10px",
                      color: "#666",
                    }}
                  >
                    Aplicação Rápida:
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "10px",
                    }}
                  >
                    {["particular", "segurado", "proporcional", "limpar"].map(
                      (opcao) => (
                        <button
                          key={opcao}
                          type="button"
                          onClick={() => aplicarDescontoRapido(opcao)}
                          style={{
                            padding: "10px 15px",
                            border: `2px solid ${
                              opcaoRapidaSelecionada === opcao
                                ? "#2196F3"
                                : "#ddd"
                            }`,
                            backgroundColor:
                              opcaoRapidaSelecionada === opcao
                                ? "#e3f2fd"
                                : "white",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "13px",
                            fontWeight: "500",
                          }}
                        >
                          {opcao === "particular" && "100% Particular"}
                          {opcao === "segurado" &&
                            `100% ${instituicaoSelecionada?.nome}`}
                          {opcao === "proporcional" && "Proporcional"}
                          {opcao === "limpar" && "Limpar"}
                        </button>
                      ),
                    )}
                  </div>
                </div>

                {/* Inputs manuais */}
                <div style={{ marginBottom: "20px" }}>
                  <div style={{ marginBottom: "20px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      Desconto Orçamento {instituicaoSelecionada?.nome}:
                    </label>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <input
                        type="number"
                        min={0}
                        value={descontoSegurado}
                        onChange={(e) => {
                          setOpcaoRapidaSelecionada(null);
                          setDescontoSegurado(
                            Math.max(
                              0,
                              Math.floor(Number(e.target.value) || 0),
                            ),
                          );
                        }}
                        style={{
                          flex: 1,
                          padding: "10px",
                          border: `2px solid ${
                            erroSegurado ? "#f44336" : "#ddd"
                          }`,
                          borderRadius: "6px",
                          fontSize: "14px",
                        }}
                      />
                      <span style={{ fontSize: "14px", color: "#666" }}>
                        $00
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        marginTop: "5px",
                      }}
                    >
                      Máximo:{" "}
                      {Intl.NumberFormat("pt-BR").format(subtotalCobertos)}$00
                    </div>
                    {erroSegurado && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#f44336",
                          marginTop: "5px",
                          fontWeight: "500",
                        }}
                      >
                        ⚠️ Desconto não pode ser maior que o valor do orçamento
                      </div>
                    )}
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      Desconto Orçamento Particular:
                    </label>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <input
                        type="number"
                        min={0}
                        value={descontoParticular}
                        onChange={(e) => {
                          setOpcaoRapidaSelecionada(null);
                          setDescontoParticular(
                            Math.max(
                              0,
                              Math.floor(Number(e.target.value) || 0),
                            ),
                          );
                        }}
                        style={{
                          flex: 1,
                          padding: "10px",
                          border: `2px solid ${
                            erroParticular ? "#f44336" : "#ddd"
                          }`,
                          borderRadius: "6px",
                          fontSize: "14px",
                        }}
                      />
                      <span style={{ fontSize: "14px", color: "#666" }}>
                        $00
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        marginTop: "5px",
                      }}
                    >
                      Máximo:{" "}
                      {Intl.NumberFormat("pt-BR").format(subtotalNaoCobertos)}
                      $00
                    </div>
                    {erroParticular && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#f44336",
                          marginTop: "5px",
                          fontWeight: "500",
                        }}
                      >
                        ⚠️ Desconto não pode ser maior que o valor do orçamento
                      </div>
                    )}
                  </div>
                </div>

                {/* Resumo */}
                <div
                  style={{
                    backgroundColor: "#f5f5f5",
                    padding: "15px",
                    borderRadius: "6px",
                    marginBottom: "20px",
                    fontSize: "14px",
                  }}
                >
                  <div
                    style={{
                      marginBottom: "10px",
                      paddingBottom: "10px",
                      borderBottom: "1px solid #ddd",
                    }}
                  >
                    <strong>Total distribuído:</strong>{" "}
                    {Intl.NumberFormat("pt-BR").format(totalDistribuido)}$00
                  </div>
                  {diferenca !== 0 && (
                    <div
                      style={{
                        marginBottom: "10px",
                        paddingBottom: "10px",
                        borderBottom: "1px solid #ddd",
                        color: avisoExcesso ? "#ff9800" : "#666",
                      }}
                    >
                      {avisoExcesso ? "⚠️ Excesso:" : "ℹ️ Restante:"}{" "}
                      {Intl.NumberFormat("pt-BR").format(Math.abs(diferenca))}
                      $00
                    </div>
                  )}
                  <div style={{ marginBottom: "5px" }}>
                    <strong>Totais finais:</strong>
                  </div>
                  <div style={{ marginLeft: "10px" }}>
                    <div>
                      • {instituicaoSelecionada?.nome}:{" "}
                      {Intl.NumberFormat("pt-BR").format(totalFinalSegurado)}$00
                    </div>
                    <div>
                      • Particular:{" "}
                      {Intl.NumberFormat("pt-BR").format(totalFinalParticular)}
                      $00
                    </div>
                  </div>
                </div>

                {/* Botões */}
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    className="cancel-btn"
                    onClick={() => {
                      setModalDescontoAberta(false);
                      setOpcaoRapidaSelecionada(null);
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    className="blue-button"
                    disabled={temErro}
                    onClick={() => {
                      setModalDescontoAberta(false);
                      setIsSubmitting(true);
                      submitHandlerDoisOrcamentos(
                        descontoSegurado,
                        descontoParticular,
                      );
                    }}
                    style={{
                      opacity: temErro ? 0.5 : 1,
                      cursor: temErro ? "not-allowed" : "pointer",
                    }}
                  >
                    Confirmar
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
    </>
  );
}

export default NewOrcamentoDireto;
